import {
  IModalDialogOptions,
  WIDGET_COMPONENT,
  ENTITY_TYPES,
  XUX_GRID_ACTIONS,
  IBaseEntityType,
  TRANSFER_PDF_HEADER_VALUES,
} from '../../types';
import { chain, get, isNil, merge } from 'lodash';
import { BTN_CANCEL, BTN_DONE } from '@text-content/button-templates';
import { WIDGET_FAMILY } from '@config/global-constants';
import { stringCaseInsensitiveComparator } from '@app/shared/common';
import { Metadata } from '@geniuspos/xenial-data-contracts/types/dm-entities/metadata';
import { ValueGetterParams } from 'ag-grid-community';
import { TransferGroupService } from './transfer-group.service';
import { I18n } from '@app/i18n/strings-keys';

const selectSiteAndToSitesModal = (
  transferGroupService: TransferGroupService,
): IModalDialogOptions => {
  return merge(
    {},
    {
      showLoader: true,
      title: I18n.Forms.TransferForm.TransferFrom,
      validation: true,
      modalClass: `modal-md`,
      buttons: [
        {
          ...BTN_DONE,
          disabled: (formValue): boolean => {
            return formValue
              ? !transferGroupService.siteModalSelected ||
                  isNil(get(formValue, 'site_id'))
              : false;
          },
          caption: I18n.Common.Print,
        },
        BTN_CANCEL,
      ],
      value: {
        site_id: '',
        items: [],
      },
      schema: {
        type: 'object',
        properties: {
          site_id: {
            type: 'object',
          },
          items: {
            type: 'array',
          },
        },
      },
      formRules: {
        fields: {
          site_id: {
            title: I18n.Forms.TransferForm.TransferFrom,
            widget: WIDGET_COMPONENT.entity_select,
            widgetFamily: WIDGET_FAMILY.XUX,
            required: true,
            trim: true,
            widgetData: {
              placeholder: I18n.Sites.Pleaseselectasite,
              entity: ENTITY_TYPES.SITE,
              dataSource: transferGroupService.transferFromDataSource,
              lazy: false,
              enableClientFiltering: true,
            },
          },
          items: {
            title: I18n.EntityNames.TransferDestination,
            descriptionObs: transferGroupService.siteModalDescription$,
            widgetFamily: WIDGET_FAMILY.XUX,
            widget: WIDGET_COMPONENT.site_selection_list,
            widgetData: {
              valueField: 'id',
              useVisibleItemCountDescription: false,
              disableColumnSelector: true,
              gridHeight: '320px',
              showHierarchyButton: false,
              searchPlaceholder: I18n.Common.Search,
              columns: [
                {
                  title: I18n.Sites.SiteId,
                  path: TRANSFER_PDF_HEADER_VALUES.StoreNumber,
                  defaultView: true,
                  sortable: true,
                  filterParams: {
                    quickFilter: true,
                  },
                  comparator: stringCaseInsensitiveComparator,
                },
                {
                  title: I18n.Sites.SiteName,
                  path: TRANSFER_PDF_HEADER_VALUES.Name,
                  defaultView: true,
                  sortable: true,
                  comparator: stringCaseInsensitiveComparator,
                  filterParams: {
                    quickFilter: true,
                  },
                },
                {
                  title: I18n.EntityNames.Tags,
                  path: TRANSFER_PDF_HEADER_VALUES.MetaData,
                  filterParams: {
                    quickFilter: true,
                  },
                  valueGetter: (params: ValueGetterParams): string => {
                    const metadata: Metadata[] = get(
                      params,
                      'data.metadata',
                      [],
                    );

                    return chain(metadata)
                      .filter({ metadata_type: 'tag' })
                      .map('name')
                      .join(', ')
                      .value();
                  },
                  getQuickFilterTextForTreeView: (
                    params: IBaseEntityType,
                  ): string => {
                    return params?.metadata
                      ? chain(params.metadata)
                          .filter({ metadata_type: 'tag' })
                          .map('name')
                          .join(', ')
                          .value()
                      : '';
                  },
                  comparator: stringCaseInsensitiveComparator,
                  defaultView: true,
                  sortable: true,
                },
              ],
              userGridOptions: {
                overlayNoRowsTemplate: I18n.Common.NoRowsToShow,
                deltaRowDataMode: false,
              },
              eventHandlers: {
                [XUX_GRID_ACTIONS.GRID_READY]:
                  transferGroupService.listModalGridAction.bind(
                    transferGroupService,
                  ),
                [XUX_GRID_ACTIONS.ITEMS_ARRAY_CHANGED]:
                  transferGroupService.listModalGridAction.bind(
                    transferGroupService,
                  ),
                [XUX_GRID_ACTIONS.ROW_SELECTION_CHANGED]:
                  transferGroupService.listModalGridAction.bind(
                    transferGroupService,
                  ),
                [XUX_GRID_ACTIONS.QUICK_FILTER_CHANGED]:
                  transferGroupService.listModalGridAction.bind(
                    transferGroupService,
                  ),
                [XUX_GRID_ACTIONS.FILTER_MODEL_CHANGED]:
                  transferGroupService.listModalGridAction.bind(
                    transferGroupService,
                  ),
              },
            },
            validators: [transferGroupService.getToSiteListRequireValidator()],
          },
        },
      },
    },
  );
};

export const groupPrintSheetModalDefinition = {
  selectSiteAndToSitesModal,
};
